/**
 * Shared schemas + types for the third-party integration surface
 * (Discord / Twitter / YouTube). Phase 14 Plan 14-03 ships only the
 * Discord-shaped pieces; Plans 14-05 / 14-07 will extend this module
 * with TwitterConfigSchema / YouTubeConfigSchema using the same
 * .strict() pattern.
 *
 * The Rust backend (Plan 14-02) emits these field names verbatim via
 * `#[serde(rename_all = "camelCase")]` — no client-side mapping layer.
 */

import { z } from 'zod'

// ─── Discord ────────────────────────────────────────────────────────────────

/**
 * Minimal Discord embed shape that Plan 14-02's POST /send accepts. Fields
 * mirror Discord's API verbatim (camelCase friendly). Image / fields /
 * author / footer / thumbnail intentionally deferred until LaunchKit
 * composer needs them — keeps the v1 surface minimal per CONTEXT.md.
 */
export const DiscordEmbedSchema = z
  .object({
    title: z.string().max(256).optional(),
    description: z.string().max(4096).optional(),
    url: z.string().url().optional(),
    color: z.number().int().min(0).max(0xffffff).optional(),
  })
  .strict()

/**
 * Wire shape for GET /api/cms/admin/integrations/discord/config.
 * Disconnected state: every field except `connected: false` is `null`.
 * `webhookUrlMasked` is the only form the client ever sees — the plaintext
 * URL is encrypted at rest on the server post-PUT (Plan 14-01 / 14-02).
 */
export const DiscordConfigSchema = z
  .object({
    connected: z.boolean(),
    label: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    webhookUrlMasked: z.string().nullable(),
  })
  .strict()

export type DiscordConfig = z.infer<typeof DiscordConfigSchema>
export type DiscordEmbed = z.infer<typeof DiscordEmbedSchema>

/**
 * Body for POST /send. Server enforces `content OR embeds`; the client
 * mirrors that check before issuing the network call (see discordApi).
 */
export interface SendDiscordBody {
  content?: string
  embeds?: DiscordEmbed[]
}

// ─── Twitter / X ────────────────────────────────────────────────────────────

/**
 * Wire shape for GET /api/cms/admin/integrations/twitter/config — Plan 14-04.
 *
 * Disconnected state: `connected: false`, every other field null/false.
 * `bearerTokenMasked` is the only form the client ever sees of the bearer;
 * the four OAuth1.0a sub-fields are NEVER returned (only `hasOauth1: bool`).
 */
export const TwitterConfigSchema = z
  .object({
    connected: z.boolean(),
    label: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    hasOauth1: z.boolean(),
    bearerTokenMasked: z.string().nullable(),
  })
  .strict()

export type TwitterConfig = z.infer<typeof TwitterConfigSchema>

/**
 * Optional OAuth1.0a sub-creds. All four required when present (signature
 * uses all four). The backend rejects partial sets at PUT-time.
 */
export const Oauth1CredsInputSchema = z
  .object({
    consumerKey: z.string().min(1),
    consumerSecret: z.string().min(1),
    accessToken: z.string().min(1),
    accessTokenSecret: z.string().min(1),
  })
  .strict()

export type Oauth1CredsInput = z.infer<typeof Oauth1CredsInputSchema>

/**
 * Body for PUT /config — bearer required, oauth1 optional, label optional.
 */
export const TwitterCredsInputSchema = z
  .object({
    bearerToken: z.string().min(1),
    oauth1: Oauth1CredsInputSchema.optional(),
    label: z.string().max(64).optional(),
  })
  .strict()

export type TwitterCredsInput = z.infer<typeof TwitterCredsInputSchema>

/**
 * Body for POST /send — text required (1..=280 chars), mediaIds optional
 * (max 4 — Twitter v2 hard limit).
 */
export interface SendTweetBody {
  text: string
  mediaIds?: string[]
}

/**
 * Result of a successful POST /send.
 */
export const TweetResultSchema = z
  .object({
    id: z.string(),
    text: z.string(),
    url: z.string().url(),
  })
  .strict()

export type TweetResult = z.infer<typeof TweetResultSchema>

/**
 * Result of a successful POST /upload-media.
 */
export const MediaUploadResultSchema = z
  .object({
    mediaId: z.string(),
  })
  .strict()

export type MediaUploadResult = z.infer<typeof MediaUploadResultSchema>

// ─── YouTube ────────────────────────────────────────────────────────────────

/**
 * Wire shape for GET /api/cms/admin/integrations/youtube/config — Plan 14-06.
 *
 * Disconnected state: `connected: false`, all other fields null. Connected
 * state populates `channelId` + `channelTitle` (cached at PUT-time when the
 * server validates the refresh token by round-tripping the discover-channel
 * call). The plaintext refresh token is encrypted at rest; the client only
 * ever sees `refreshTokenMasked` (last 6 chars + asterisks).
 */
export const YouTubeConfigSchema = z
  .object({
    connected: z.boolean(),
    channelId: z.string().nullable(),
    channelTitle: z.string().nullable(),
    label: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    refreshTokenMasked: z.string().nullable(),
  })
  .strict()

export type YouTubeConfig = z.infer<typeof YouTubeConfigSchema>

/**
 * Body for PUT /config — refresh token from Google's OAuth Playground
 * (Plan 14-06 D-02 lock: paste-from-playground rather than full OAuth flow).
 * Optional label distinguishes channels in the masked status card if Craig
 * ever connects a second channel via re-PUT.
 */
export const YouTubeCredsInputSchema = z
  .object({
    refreshToken: z.string().min(1),
    label: z.string().max(64).optional(),
  })
  .strict()

export type YouTubeCredsInput = z.infer<typeof YouTubeCredsInputSchema>

/**
 * Single video as returned by GET /videos and PATCH /videos/:id. Mirrors
 * the Rust `YouTubeVideo` struct verbatim (server emits camelCase via
 * #[serde(rename_all = "camelCase")]).
 *
 * `viewCount` is 0 on PATCH responses (Google's videos.update PUT excludes
 * the statistics part — the server zero-fills rather than doing a follow-up
 * GET; see 14-06-SUMMARY decisions).
 */
export const YouTubeVideoSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    publishedAt: z.string(),
    thumbnailUrl: z.string().nullable(),
    privacyStatus: z.enum(['public', 'unlisted', 'private']),
    viewCount: z.number(),
    url: z.string().url(),
  })
  .strict()

export type YouTubeVideo = z.infer<typeof YouTubeVideoSchema>

/**
 * GET /videos response wrapper. Matches the gitlab/discord list-response
 * pattern: a top-level `items: T[]` so future pagination metadata can land
 * alongside without breaking schema parsers.
 */
export const YouTubeVideosListSchema = z
  .object({
    items: z.array(YouTubeVideoSchema),
  })
  .strict()

export type YouTubeVideosList = z.infer<typeof YouTubeVideosListSchema>

/**
 * Body for PATCH /videos/:id — partial update. All fields optional; server
 * does a get-then-merge on YouTube's videos.update PUT (which rejects
 * partial snippets). `tags` are an array; the UI splits a comma-separated
 * input into this shape.
 */
export const UpdateVideoMetadataSchema = z
  .object({
    title: z.string().min(1).max(100).optional(),
    description: z.string().max(5000).optional(),
    tags: z.array(z.string().max(30)).max(30).optional(),
    privacyStatus: z.enum(['public', 'unlisted', 'private']).optional(),
  })
  .strict()

export type UpdateVideoMetadata = z.infer<typeof UpdateVideoMetadataSchema>
